import React, { Component, Fragment } from 'react';
import { AlertTriangle, Hash, Lock, Users } from 'react-feather';
import { Link } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as StringConstants from '../../constants/StringConstants';

import * as AppConstants from '../../constants/AppConstants';
import * as ChatRoles from '../../constants/Roles';

import Avatar from '../Avatar/AvatarContainer';
import User from '../User/UserContainer';
import MobileMenuToggle from '../MobileMenuToggle/MobileMenuToggleContainer';
import * as routes from '../../constants/UiRoutes';
import { logError } from '../../helpers/BrowserUtils';

/** Displays a directory of all chats, users, and public channels
 *  for the user to browse and find new items. */
export default class BrowsePanel extends Component {
  static propTypes = {
    allResults: PropTypes.array,
    identity: PropTypes.object,
    initialFilterType: PropTypes.string,
  };

  componentDidMount() {
    const contentArea = document.querySelector('.content-area');
    contentArea.scrollTop = 0;
  }

  constructor(props) {
    super(props);
    const { initialFilterType } = props;
    this.state = {
      filterType: initialFilterType ? initialFilterType : AppConstants.FILTER_ALL,
      filterTerm: '',
    };
  }

  componentDidUpdate(prevProps) {
    const { initialFilterType } = this.props;
    if (prevProps.initialFilterType !== initialFilterType) {
      this.setState ({
        filterType: initialFilterType ? initialFilterType : AppConstants.FILTER_ALL,
      });
    }
  }

  filterResults = (filterType) => {
    this.setState({ filterType });
  };

  filterItems = (filterTerm) => {
    this.setState({ filterTerm });
  };

  matchesFilterTerm = (str, filterTerm) => {
    return str.trim().toLowerCase().indexOf(filterTerm.trim().toLowerCase()) > -1;
  };

  matchesFilterCategory = (itemType, filterCategory) => {
    switch (filterCategory) {
      case AppConstants.FILTER_ALL:
        return true;
      case AppConstants.FILTER_CHANNELS:
        return !!(this.isPrivateChannel(itemType) || this.isPublicChannel(itemType));
      case AppConstants.FILTER_PEOPLE:
        return !!(this.isDM(itemType) || this.isUser(itemType));
      default:
        return;
    }
  };

  getIcon = (chat) => {
    const identityId = this.props.identity.id;
    switch (chat.type) {
      case AppConstants.PRIVATE_CHANNEL:
        return <Lock size={16} />;
      case AppConstants.PUBLIC_CHANNEL:
        return <Hash size={16} />;
      case AppConstants.USER:
        return <Avatar
          userId={chat.id}
          size={AppConstants.USER_SIZE_SMALL}
          type={chat.id === identityId ? ChatRoles.SENDER : ''}
        />;
      case AppConstants.DIRECT_MESSAGE:
        if (chat.users.length > 2) {
          return <Users size={16} />;
        } else if (chat.users.length === 1) {
          return <Avatar
            userId={chat.users[0]}
            size={AppConstants.USER_SIZE_SMALL}
            type={chat.users[0] === identityId ? ChatRoles.SENDER : ''}
          />;
        } else {
          return chat.users
            .filter(id => id !== identityId)
            .map(id =>
              <Avatar
                key={id}
                userId={id}
                size={AppConstants.USER_SIZE_SMALL}
                type={id === identityId ? ChatRoles.SENDER : ''}
              />);
        }
      default:
        return;
    }
  };

  isUser = itemType => itemType === AppConstants.USER;
  isPublicChannel = itemType => itemType === AppConstants.PUBLIC_CHANNEL;
  isPrivateChannel = itemType => itemType === AppConstants.PRIVATE_CHANNEL;
  isDM = itemType => itemType === AppConstants.DIRECT_MESSAGE;

  // checks if item matches based on there being a name,
  // the type matches the selected category (all, channels, people),
  // and the name matches the search term (if any)
  matchesFilterCriteria = (item, filterTerm, filterType) => {
    return item.name && this.matchesFilterTerm(item.name, filterTerm) && this.matchesFilterCategory(item.type, filterType);
  };

  getItemLink = (item) => {
    // if its a DM or private channel, we know they're in that chat, so take them directly to chat
    if (item.type === AppConstants.PRIVATE_CHANNEL || item.type === AppConstants.DIRECT_MESSAGE) {
      return `/chat/${item.id}`;
    } else if (item.type === AppConstants.PUBLIC_CHANNEL) {
      // if you're already in the public channel, go to it. If you're not, join it
      return item.users && item.users.includes(this.props.identity.id) ? (
        // go to chat
        `/chat/${item.id}`
      ) : (
        // join channel
        `/chat/${item.id}/join`
      );
    } else if (item.type === AppConstants.USER && !item.users.length) {
      // pre-populate multiselect on create dm page with selected user
      return {
        pathname: '/chat/create/dm',
        state: { usersToInvite: item.id },
      };
    }
    return true;
  };

  noPublicChannelsExist = () => this.props.allResults.filter(item => this.matchesFilterCriteria(item, '', AppConstants.FILTER_CHANNELS)).length === 0

  render() {
    const { allResults } = this.props;
    const { filterTerm, filterType } = this.state;
    return (
      <div className="browse-panel flex flex-column" data-qa="browsePanel">
        <div className="browse-panel-overview flex flex-column">
          <div className="browse-panel-heading flex"  data-qa="browsePanel-heading">
            <div className="menu-plus-search">
              <MobileMenuToggle />
              <div className="search-box">
                <label htmlFor="directorySearch" className="WCAG-label">Directory Search</label>
                <input
                  id="directorySearch"
                  onChange={e => this.filterItems(e.target.value)}
                  value={filterTerm}
                  type="text"
                  placeholder="Search ..."
                  data-qa="browseFilter"
                  autoFocus={!isMobile}
                />
              </div>
            </div>
            <div className="toggle-group" data-qa="browsePanel-filter">
              <button
                onClick={() => this.filterResults(AppConstants.FILTER_ALL)}
                className={classnames({
                  'active': filterType === AppConstants.FILTER_ALL,
                })}
              >
                All
              </button>
              <button
                onClick={() => this.filterResults(AppConstants.FILTER_CHANNELS)}
                className={classnames({
                  'active': filterType === AppConstants.FILTER_CHANNELS,
                })}
              >
                Channels
              </button>
              <button
                onClick={() => this.filterResults(AppConstants.FILTER_PEOPLE)}
                className={classnames({
                  'active': filterType === AppConstants.FILTER_PEOPLE,
                })}
              >
                People
              </button>
            </div>
          </div>
        </div>

        <div className="browse-panel-content">
          <div className="filtered-results" data-qa="browsePanel-results">
            {allResults &&
              <Fragment>
                {allResults
                  .filter(item => this.matchesFilterCriteria(item, filterTerm, filterType))
                  .map((item, i) =>
                    <Link
                      to={this.getItemLink(item)}
                      className={classnames({
                        'item': true,
                        'item-single-user': this.isUser(item.type) || (this.isDM(item.type) && item.users.length <= 2),
                      })}
                      key={`all-${i}`}
                      data-qa="browsePanel-result"
                    >
                      <span className="item-name">
                        {this.isUser(item.type) ? (
                          <User
                            id={parseInt(item.id, 10)}
                            hideAvatar
                            noPad
                            statusMarginRight="12px"
                            fontSizeName="14px"
                          />
                        ) : (
                          <Fragment>
                            {this.isDM(item.type) && item.users.length <= 2
                              ? <User
                                id={item.mainUserId}
                                hideAvatar
                                noPad
                                statusMarginRight="12px"
                                fontSizeName="14px"
                              />
                              : <Fragment>
                                <span
                                  className="item-indicator"
                                  data-qa={`browsePanel-icon-${item.type}`}
                                >
                                  {this.getIcon(item)}
                                </span>
                                {item.name}
                              </Fragment>
                            }
                          </Fragment>
                        )}
                      </span>
                    </Link>
                  )
                }
              </Fragment>
            }
            {allResults
              .filter(item => this.matchesFilterCriteria(item, filterTerm, filterType))
              .length === 0 &&
              this.renderEmptyState(filterType, filterTerm)
            }
          </div>
        </div>
      </div>
    );
  }

  renderEmptyState(filterType, filterTerm) {
    const createChannelLink = (
      <Link className={classnames('btn', 'primary')} to={routes.CREATE_CHANNEL_URL} >
        {StringConstants.CREATE_CHANNEL}
      </Link>
    );
    const createDirectMessageLink = (
      <Link className={classnames('btn', 'primary')} to={routes.CREATE_DM_URL}>
        {StringConstants.CREATE_DM}
      </Link>
    );

    let additionalInformation = '';
    switch (filterType) {
      case AppConstants.FILTER_CHANNELS:
        if (filterTerm) {
          additionalInformation = (
            <Fragment>
              <p>
                {StringConstants.EMPTY_STATE_FILTER_CHANNELS_FILTERTERM_LINE1}
                <br/>
                {StringConstants.EMPTY_STATE_FILTER_CHANNELS_FILTERTERM_LINE2}
              </p>
              {createChannelLink}
            </Fragment>
          );
        } else {
          additionalInformation = (
            <Fragment>
              <p>
                {StringConstants.EMPTY_STATE_FILTER_CHANNELS_NO_FILTERTERM_LINE1}
                <br/>
                {StringConstants.EMPTY_STATE_FILTER_CHANNELS_NO_FILTERTERM_LINE2}
              </p>
              {createChannelLink}
            </Fragment>
          );
        }
        break;

      case AppConstants.FILTER_ALL:
        additionalInformation = (
          <Fragment>
            <p>{StringConstants.EMPTY_STATE_FILTER_CHANNELS}</p>
            <div className="create-buttons-container">
              {createDirectMessageLink}{createChannelLink}
            </div>
          </Fragment>
        );
        break;
      case AppConstants.FILTER_PEOPLE:
        additionalInformation = (
          <p>{StringConstants.EMPTY_STATE_FILTER_PEOPLE}</p>
        );
        break;
      default:
        logError('Unexpected filter type in BrowsePanel: ', filterType);
        break;
    }

    return (
      <div
        className="empty-state flex flex-column"
        data-qa="browsePanel-empty"
      >
        <AlertTriangle size={40} />
        <h2>{StringConstants.NO_RESULTS_FOUND}</h2>
        {additionalInformation}
      </div>
    );
  }
};
